import currency from 'currency.js';

import type { CartTrackingProperties } from '$src/business/atoms/global-properties-track';
import type { Cart } from '@getjust/gateway';

import { SelectedPaymentMethodAtom } from '$src/business/atoms/payment-method';

export const getCartTrackingProperties = ({
  cart,
  selectedPaymentMethod,
}: {
  cart?: Cart;
  selectedPaymentMethod?: SelectedPaymentMethodAtom;
}): { cart: CartTrackingProperties; totalCartQuantity: number } | undefined => {
  if (!cart) {
    return undefined;
  }

  const selectedShippingMethod = cart.shipping?.selected?.[0];

  const totalCartQuantity = cart?.lineItems?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;

  const cartTrackingProperties = {
    id: cart.id,
    currency: cart.currency,
    items: (cart.lineItems ?? []).map((lineItem) => ({
      productId: lineItem.productId,
      variantId: lineItem.variantId,
      unitPrice: lineItem.unitAmount,
      count: lineItem.quantity,
      totalDiscountedPrice: currency(lineItem.totalAmount).subtract(lineItem.discountAmount ?? 0).value,
      productTitle: lineItem.name,
      variantTitle: lineItem.description ?? '',
    })),
    shipping: {
      country: cart.shipping?.address?.countryCode ?? '',
      city: cart.shipping?.address?.city ?? '',
    },
    selectedShippingMethod: selectedShippingMethod
      ? {
          id: selectedShippingMethod.id,
          title: selectedShippingMethod.aggregatedTitle,
          type: selectedShippingMethod.carriers?.[0]?.type ?? '',
          price: selectedShippingMethod.totalAmount,
          totalDiscountedPrice: currency(selectedShippingMethod.totalAmount).subtract(
            selectedShippingMethod.discountAmount ?? 0,
          ).value,
        }
      : undefined,
    paymentMethod: {
      type: selectedPaymentMethod?.type ?? '',
    },
    discounts: (cart.discounts ?? []).filter(Boolean).map((discount) => ({
      code: discount.code ?? '',
    })),
    price: {
      cartAmount: cart.subTotal,
      shippingAmount: cart.totalShipping,
      totalAmount: cart.totalAmount,
    },
  };

  return { cart: cartTrackingProperties, totalCartQuantity };
};
